import { useRef, useReducer, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  coursesNames,
  navRoutes,
  organisationStatuses,
  paymentPlans,
} from '../../../constants';
import { Col, Row } from '../../../components/Grid';
import * as T from '../../../components/Typography';
import {
  BasicInput,
  DatePicker,
  Dropdown,
  Checkbox,
} from '../../../components/Inputs';
import { BasicButton } from '../../../components/Button';
import { addOrganisation as validate } from '../../../validation/schemas';
import {
  useAddOrganisation,
  useGetOrganisationByIdOrUniqueSlug,
  useUpdateOrganisation,
} from '../../../api-calls/organisations.queries';
import Modal from '../../../components/Modal';
import { BackButton } from 'components/BackButton';
import { ConfirmationSuccessModal, Tip } from 'components';
import useDebounce from '../../../Hooks/UseDebounce';
import { useVerifyNewSubdomain } from '../../../api-calls/site-settings.queries';
import { getOrganisationInviteUrl } from 'helpers/get-organisations-links';
import makeError from 'api-calls/format-error';
import ExistingEmailConsentModal from './ExistingEmailConsentModal';

const initialState = {
  form: {
    name: '',
    sfAccountId: '',
    firstName: '',
    lastName: '',
    email: '',
    contractEndDate: null,
    paymentPlan: '',
    isWorkingInPrison: false,
    subdomain: '',
    isTpdCourse: false,
    isCmidCourse: false,
  },
  validationErrs: { contactLinks: {} },
  originalEmail: '',
};

function reducer(state, newState) {
  let value = newState;
  if (typeof newState === 'function') {
    value = newState(state);
  }

  return { ...state, ...value };
}

const getCleanEmail = (email) => {
  return email.replace(/ /g, '').toLowerCase();
};

const AddOrganisation = () => {
  const [isUpdateEmailModalVisible, setIsUpdateEmailModalVisible] =
    useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isConsentModalVisible, setIsConsentModalVisible] = useState({
    show: false,
    organisationNames: [],
  });

  const { id } = useParams();

  const navigate = useNavigate();

  const [state, setState] = useReducer(reducer, initialState);
  const submitAttempt = useRef(false);
  const validateSubDomainAttempt = useRef(false);
  const subdomainRef = useRef(null);

  const {
    form: {
      name,
      sfAccountId,
      firstName,
      lastName,
      email,
      contractEndDate,
      paymentPlan,
      isWorkingInPrison,
      isWorkingInPrisonEnvironment,
      subdomain,
      isTpdCourse,
      isCmidCourse,
    },
    validationErrs,
    originalEmail,
  } = state;

  const {
    mutateAsync: addOrganisation,
    error: addError,
    isLoading: addOrganisationLoading,
    isError: isAddError,
    isSuccess,
    data: { inviteToken } = {},
  } = useAddOrganisation();

  const {
    mutateAsync: updateOrganisation,
    isLoading: updateOrganisationLoading,
    isSuccess: updateIsSuccess,
    error: updateError,
    isError: isUpdateError,
  } = useUpdateOrganisation({ id });

  const error = addError || updateError;
  const isLoading = addOrganisationLoading || updateOrganisationLoading;
  const isError = isAddError || isUpdateError;

  const { data: organisation = {}, isFetched: organisationLoaded } =
    useGetOrganisationByIdOrUniqueSlug({ id }, { enabled: !!id });
  const debouncedSubdomain = useDebounce(subdomain, 500);
  const {
    data: verifiedSubDomain,
    isLoading: isVerifying,
    error: subdomainErr,
  } = useVerifyNewSubdomain(
    {
      subdomain: debouncedSubdomain,
      id,
    },
    {
      enabled:
        !!isWorkingInPrisonEnvironment &&
        !!debouncedSubdomain &&
        validateSubDomainAttempt.current,
    }
  );

  // Used when updating an existing organisation's courses
  const isRemovingBothCourses = !isTpdCourse && !isCmidCourse;

  const setFormData = (data) =>
    setState((prevState) => ({ form: { ...prevState.form, ...data } }));

  const validateForm = () => {
    try {
      validate(state.form);
      setState({ validationErrs: { hasError: false } });
      return true;
    } catch (error) {
      if (error.name === 'ValidationError') {
        setState({
          validationErrs: { ...error.inner, hasError: true },
        });
      }
      return false;
    }
  };

  const onError = (error, confirmed) => {
    const err = makeError(error);
    if (
      err?.statusCode === 409 &&
      err?.message?.includes('email already exists') &&
      err?.data?.isOrgAdmin
    ) {
      if (confirmed) {
        setIsUpdateEmailModalVisible(false);
      }

      setIsConsentModalVisible({
        show: true,
        organisationNames: err?.data?.organisationNames,
      });
    }
  };
  let subdomainError = '';
  if (subdomainErr?.message || verifiedSubDomain?.isSubdomainExists) {
    if (subdomainErr?.data?.inner?.[0]) {
      subdomainError = subdomainErr.data.inner[0].message;
    } else {
      subdomainError =
        subdomainErr?.message || 'Subdomain already exists, try another one';
    }
  } else {
    subdomainError = '';
  }
  const handleSubmit = async (e, confirmed, addToExistingAdmin) => {
    e.preventDefault();
    submitAttempt.current = true;

    const isValid = validateForm();
    if (isValid && !subdomainError) {
      if (id) {
        if (
          getCleanEmail(email) === getCleanEmail(originalEmail) ||
          confirmed
        ) {
          await updateOrganisation(
            {
              ...state.form,
              email: getCleanEmail(email),
              ...(isRemovingBothCourses && {
                status: organisationStatuses.DEACTIVATED,
              }),
              isEmailUpdated:
                getCleanEmail(email) !== getCleanEmail(originalEmail),
              addToExistingAdmin,
            },
            { onError: (err) => onError(err, confirmed) }
          );
        } else {
          setIsUpdateEmailModalVisible(true);
        }
        // setState({ originalEmail: getCleanEmail(email) });
      } else {
        if (!isTpdCourse && !isCmidCourse) {
          setState({
            validationErrs: {
              hasError: false,
              isCmidCourse:
                'You must at least choose one course when adding an organisation',
            },
          });
          return;
        }
        await addOrganisation(
          { ...state.form, email: getCleanEmail(email), addToExistingAdmin },
          { onError }
        );
      }
    }
  };

  useEffect(() => {
    if (submitAttempt.current) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    name,
    sfAccountId,
    firstName,
    lastName,
    email,
    contractEndDate,
    paymentPlan,
    isWorkingInPrison,
    isWorkingInPrisonEnvironment,
    subdomain,
    isTpdCourse,
    isCmidCourse,
  ]);

  useEffect(() => {
    if (!isError) return;

    if (error?.statusCode === 409) {
      setState({
        validationErrs: {
          hasError: true,
          [error?.data.field]: error?.message,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    if (updateIsSuccess) {
      setIsUpdateEmailModalVisible(false);
      setIsSuccessModalVisible(true);
    }
  }, [updateIsSuccess]);

  useEffect(() => {
    if (organisationLoaded && id) {
      setState({
        form: {
          name: organisation?.name,
          sfAccountId: organisation?.sfAccountId,
          firstName: organisation?.firstName,
          lastName: organisation?.lastName,
          email: organisation?.email,
          contractEndDate: organisation?.contractEndDate,
          paymentPlan: organisation?.paymentPlan,
          isWorkingInPrison: organisation?.isWorkingInPrison,
          isWorkingInPrisonEnvironment:
            organisation?.isWorkingInPrisonEnvironment,
          subdomain: organisation?.subdomain,
          isTpdCourse: organisation?.isTpdCourse,
          isCmidCourse: organisation?.isCmidCourse,
        },
        originalEmail: organisation?.email,
      });
    }
  }, [id, organisation, organisationLoaded]);

  const inviteLink = getOrganisationInviteUrl({
    inviteToken,
    subdomain,
    isWorkingInPrisonEnvironment,
  });

  useEffect(() => {
    if (isSuccess) {
      navigate(navRoutes.ADMIN.ORGANISATION_ADDED, {
        state: { inviteLink },
      });
    }
  }, [isSuccess, navigate, inviteLink]);

  const handleInPrisonEnvironmentChange = (_isWorkingInPrisonEnvironment) => {
    const updatedData = {
      isWorkingInPrisonEnvironment: _isWorkingInPrisonEnvironment,
    };

    // include an automatically checked checkbox for "Turning Pages for Prison Use".
    if (!isWorkingInPrisonEnvironment && _isWorkingInPrisonEnvironment) {
      updatedData.isTpdCourse = true;
      updatedData.isCmidCourse = false;
    }

    if (_isWorkingInPrisonEnvironment && !subdomain) {
      const newSubdomain = name
        ? name.replace(/ /g, '-').toLowerCase()
        : `org-${Math.random().toString(36).substring(2, 8)}`;

      updatedData.subdomain = newSubdomain;
      validateSubDomainAttempt.current = true;
    }

    setFormData(updatedData);
  };

  useEffect(() => {
    if (
      isWorkingInPrisonEnvironment &&
      validateSubDomainAttempt.current &&
      !isVerifying
    ) {
      subdomainRef.current.focus();
    }
  }, [isWorkingInPrisonEnvironment, isVerifying]);

  const domain = window.location.hostname;
  return (
    <Row jc="center">
      <Col w={[4, 10, 10]}>
        <BackButton link={navRoutes.ADMIN.ORGANISATIONS} />
      </Col>
      <Col w={[4, 10, 10]} mt="6">
        <Row>
          <Col w={[4, 12, 12]}>
            <T.H1 color="neutralMain" weight="bold">
              {id ? 'Edit organisation' : 'Add organisation'}
            </T.H1>
            <T.P mt={4} color="neutral90">
              {id
                ? `Here you can edit the organisation. Please note changing the main account holder details email will change the email address with which the main account will log in.`
                : `Here you can set up organisations to start using the Turning Pages
            platform. When you add the organisation they will receive an invite
            link to create their account.`}
            </T.P>
          </Col>
        </Row>
        <Row mt="6">
          <Col w={[4, 6, 6]} mbM="4">
            <BasicInput
              label="Organisation name"
              placeholder="Organisation name..."
              value={name}
              name="name"
              handleChange={(name) => setFormData({ name })}
              error={validationErrs.name}
            />
          </Col>
          <Col w={[4, 6, 6]}>
            <BasicInput
              label="Salesforce ID"
              placeholder="Salesforce ID..."
              value={sfAccountId}
              name="sfAccountId"
              handleChange={(sfAccountId) => setFormData({ sfAccountId })}
              error={validationErrs.sfAccountId}
            />
          </Col>
        </Row>

        <Row mt={'7'} mtM="6">
          <Col w={[4, 6, 6]}>
            <Checkbox
              plain
              label="Add probation officer contact, NOMIS ID and nDelius CRN to learner information."
              checked={isWorkingInPrison}
              name="isWorkingInPrison"
              handleChange={(isWorkingInPrison) => {
                setFormData({ isWorkingInPrison });
              }}
              error={validationErrs.isWorkingInPrison}
            />
          </Col>
          <Col w={[4, 6, 6]}>
            <Checkbox
              plain
              label="This organization plans to use the app within a prison setting and requires a secure URL for access."
              checked={isWorkingInPrisonEnvironment}
              name="isWorkingInPrisonEnvironment"
              handleChange={handleInPrisonEnvironmentChange}
              error={validationErrs.isWorkingInPrisonEnvironment}
              disabled={isVerifying}
            />
          </Col>
        </Row>
        {isWorkingInPrisonEnvironment && (
          <>
            <Row mt="2" jc="end" ai="center">
              <Col w={[4, 3, 3]}>
                <BasicInput
                  ref={subdomainRef}
                  value={subdomain}
                  name="subdomain"
                  minHeight="40px"
                  borderRadius="8px"
                  borderColor="quinaryMain"
                  textAlign="end"
                  hideErrorMsg
                  handleChange={(subdomain) => {
                    validateSubDomainAttempt.current = true;
                    setFormData({ subdomain });
                  }}
                  disabled={isVerifying}
                  error={validationErrs.subdomain || subdomainError}
                />
              </Col>
              <Col w={[4, 3, 3]} ml="-8px">
                <T.P>.{domain}</T.P>
              </Col>
            </Row>
            {(validationErrs.subdomain || subdomainError) && (
              <Row mt="2" jc="end">
                <Col w={[4, 6, 6]}>
                  <T.P color="error" ml="2">
                    {validationErrs.subdomain || subdomainError}
                  </T.P>
                </Col>
              </Row>
            )}
          </>
        )}
        <Row mt={'40px'} mtM="6">
          <Col w={[4, 12, 12]} mb="5">
            <T.H2>Main account holder details</T.H2>
          </Col>

          <Col w={[4, 6, 6]} mbM="4">
            <BasicInput
              label="First name"
              placeholder="First name..."
              value={firstName}
              name="firstName"
              handleChange={(firstName) => setFormData({ firstName })}
              error={validationErrs.firstName}
            />
          </Col>
          <Col w={[4, 6, 6]}>
            <BasicInput
              label="Last name"
              placeholder="Last name..."
              value={lastName}
              name="lastName"
              handleChange={(lastName) => setFormData({ lastName })}
              error={validationErrs.lastName}
            />
          </Col>
          <Col w={[4, 6, 6]} mt={4}>
            <BasicInput
              label="Email address"
              placeholder="Email address..."
              value={email}
              name="email"
              handleChange={(email) => setFormData({ email })}
              error={validationErrs.email}
            />
          </Col>
        </Row>
        <Row mt={'40px'}>
          <Col w={[4, 12, 12]} mb="5">
            <T.H2>Access details</T.H2>
          </Col>

          <Col w={[4, 6, 6]} mbM="4">
            <DatePicker
              label="Contract end date"
              placeholder="Contract end date..."
              value={contractEndDate}
              name="contractEndDate"
              handleChange={(contractEndDate) =>
                setFormData({ contractEndDate })
              }
              validDateLimit={null}
              error={validationErrs.contractEndDate}
            />
          </Col>

          <Col w={[4, 6, 6]}>
            <Dropdown
              label="Payment plan"
              placeholder="Payment plan..."
              selected={paymentPlans.find(
                (_paymentPlan) => _paymentPlan.value === paymentPlan || ''
              )}
              options={paymentPlans}
              name="paymentPlan"
              handleChange={(paymentPlan) => setFormData({ paymentPlan })}
              error={validationErrs.paymentPlan}
            />
          </Col>
        </Row>

        <Row mt={'7'} mtM="6">
          <Col w={[4, 12, 12]} mb="5">
            <T.H2>
              Select authorised courses to give this organisation access
            </T.H2>
          </Col>
          <Col w={[4, 12, 12]}>
            <Checkbox
              plain
              label={coursesNames.TPD}
              checked={isTpdCourse}
              name="isTpdCourse"
              handleChange={(isTpdCourse) => {
                setFormData({ isTpdCourse });
              }}
              error={validationErrs.isTpdCourse}
              disabled={isWorkingInPrisonEnvironment}
            />
          </Col>
          <Col w={[4, 12, 12]}>
            <Checkbox
              plain
              label={coursesNames.CMID}
              checked={isCmidCourse}
              name="isCmidCourse"
              handleChange={(isCmidCourse) => {
                setFormData({ isCmidCourse });
              }}
              error={validationErrs.isCmidCourse}
              disabled={isWorkingInPrisonEnvironment}
            />
          </Col>
          <Col w={[4, 12, 12]}>
            {id && isRemovingBothCourses && (
              <Tip
                variant="error"
                ShowBoldTipPrefix
                text={
                  'We will still keep hold of all the data, but the organisation and any of their coaches and learners will no longer be able to access that course via the platform'
                }
              />
            )}
          </Col>
        </Row>

        <Row mt="6">
          {error?.message ? (
            <Col w={[4, 12, 12]}>
              <T.P color="error" mb={3}>
                {error?.message}
              </T.P>
            </Col>
          ) : null}
          {validationErrs.hasError && (
            <Col w={[4, 12, 12]}>
              <T.P color="error" mb={3}>
                Please fill all the required fields
              </T.P>
            </Col>
          )}
          <Col w={[4, 12, 12]}>
            <BasicButton
              variant="primary"
              disabled={
                validationErrs.hasError || isLoading || !!subdomainError
              }
              loading={isLoading}
              handleClick={handleSubmit}
              maxWidth="300px"
            >
              <T.P color="white" weight="semi">
                Submit
              </T.P>
            </BasicButton>
          </Col>
        </Row>
      </Col>

      <Modal
        visible={isUpdateEmailModalVisible}
        setIsModalVisible={setIsUpdateEmailModalVisible}
      >
        <Row>
          <Col w={[4, 12, 12]}>
            <T.P size="med" weight="bold" color="white">
              Are you sure?
            </T.P>
            {organisation.linkedUsers?.length > 1 ? (
              <T.P size="regular" color="white" mt={4} mb={4}>
                The original email is already linked to other users in the
                system. Changing the email will change the log in details for
                other users. Please confirm to proceed.
              </T.P>
            ) : (
              <T.P size="regular" color="white" mt={4} mb={4}>
                By changing their email address you are changing their log in
                details, so please update the main account holder immediately.
              </T.P>
            )}

            {updateError?.message && (
              <T.P color="error" mb="-10px">
                {updateError?.message}
              </T.P>
            )}

            <BasicButton
              variant="secondary"
              onClick={(e) => {
                handleSubmit(e, true);
              }}
              mt={4}
            >
              Confirm
            </BasicButton>
            <BasicButton
              variant="tertiary"
              onClick={() => {
                setIsUpdateEmailModalVisible(false);
              }}
              mt={2}
            >
              Cancel
            </BasicButton>
          </Col>
        </Row>
      </Modal>
      <ConfirmationSuccessModal
        visible={isSuccessModalVisible}
        setIsModalVisible={setIsSuccessModalVisible}
        title="Success"
        description={'You have successfully updated this organisation'}
        confirmText="Return to Dashboard"
        onConfirm={() => {
          setIsSuccessModalVisible(false);
          navigate(navRoutes.ADMIN.DASHBOARD);
        }}
        cancelText="Return to Organisations"
        onCancel={() => {
          setIsSuccessModalVisible(false);
          navigate(navRoutes.ADMIN.ORGANISATIONS);
        }}
      />
      <ExistingEmailConsentModal
        email={email}
        isModalVisible={isConsentModalVisible}
        setIsModalVisible={setIsConsentModalVisible}
        handleSubmit={handleSubmit}
        isEditing={!!id}
      />
    </Row>
  );
};

export default AddOrganisation;
